import { useEffect, useRef, useState } from "react";
import { Alert, CheckboxProps, Col, Divider, Flex, Form, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";

import { PageMessage } from "components/PageMessage";
import { defaultMessage } from "components/PageMessage/PageMessage";
import {
  DirectionType,
  OPERATION_TYPE,
  NOTIFICATION_TYPE_ENUM,
  WalletEntity,
  NETWORK_TEXT,
  REQUEST_CHECK_STATUS_ENUM,
  RISK_LEVEL_ENUM,
  WalletRiskLevel,
  CONTRACT_DIRECTION_ENUM,
} from "interfaces";
import {
  generateOperation,
  createQuote,
  getDefaultQuoteExpTime,
  getNativeCurrencyRate,
  checkWalletWhitelist,
} from "services";
import { useStores } from "stores";
import {
  DefaultInput,
  CustomButton,
  DefaultCheckbox,
  ExpTimeInput,
  DefaultSelect,
  NumberInput,
  OtpInput,
  OtpTimeLeft,
} from "ui";
import { clearCertDB, signatureMessage } from "utils/processCert";
import { getWalletRiskMessage, hashMessage, isValidNetworkAddress, WALLET_NETWORK } from "utils";
import {
  _greaterThan,
  _isNumber,
  _isRequired,
  _isValidNetworkAddress,
  canSendSms,
  clearSmsData,
  convertFromISOHourMinuteDuration,
  convertToISODuration,
  dateFormat,
  getBankAlertMessage,
  getExpirationTime,
  getLastSmsData,
  markSmsSend,
  parseErrorMessage,
  renderBankOptions,
  useWalletRisk,
  validateExpirationTime,
} from "utils";
import { DIRECT_PAYMENT_CUSTOMER_LP, SMS_CODE_RETRY_DELAY_IN_SECONDS } from "utils/envConstants";
import { useNotificationContext } from "providers";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { AmlAlert } from "components";
import { CHANGE_WALLET_ADDRESS_MESSAGE } from "constants/network";
import { UsdtRateInput } from "./UsdtRateInput";
import { trimMask } from "utils";
import { useDebounce } from "utils/useDebounce";
import { CustomAlert } from "ui/CustomAlert";

enum OFFER_STEP {
  OFFER = "OFFER",
  CHECKING_WALLET = "CHECKING_WALLET",
  SMS = "SMS",
}

const DIRECTION_TEXT: Record<DirectionType, string> = {
  EXP: "покупки",
  IMP: "продажи",
};

export const OfferQuoteModal = observer(() => {
  const { actionModals, bidCard, user, bankAccountsStore, companyCard, quotes, settings } = useStores();
  const { openNotification } = useNotificationContext();
  const { contract, network, currency } = bidCard.bid;

  const [loading, setLoading] = useState(false);
  const [offerStep, setOfferStep] = useState(OFFER_STEP.OFFER);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLeftInterval, setTimeLeftInterval] = useState<NodeJS.Timeout>();
  const [canSendCode, setCanSendCode] = useState(true);
  const [currentMessageHash, setCurrentMessageHash] = useState<string>("");

  const [form] = Form.useForm();
  const isHiddenFormItems = offerStep !== OFFER_STEP.OFFER;
  const [isUseDefaultWallet, setIsUseDefaultWallet] = useState(true);

  const [expTimeFrame, setExpTimeFrame] = useState(false);
  const [expTime, setExpTime] = useState("");

  const [destinationWalletFromCard, setDestinationWalletFromCard] = useState<WalletEntity | undefined>(undefined);

  const [isWalletChecked, setIsWalletChecked] = useState(false);
  const [canCheckWallet, setCanCheckWallet] = useState(false);
  const [checkedWallet, setCheckedWallet] = useState<string | null>(null);
  const [savedRiskLevel, setSavedRiskLevel] = useState<WalletRiskLevel>();
  const [walletAmlIdList, setWalletAmlIdList] = useState<string[]>([]);

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [destinationWalletValue, setDestinationWalletValue] = useState("");
  const [isCheckWhiteListWallet, setIsCheckWhiteListWallet] = useState(false);
  const [whitelistError, setWhitelistError] = useState<string | null>(null);

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const companyId = user.userInfo?.company_id;
  const isImpContract = contract.direction === "IMP";
  const isExpContract = contract.direction === "EXP";
  const isWhitelistEnabled = settings.companySettings.whitelistEnabled;

  const { isSeniorLP, isJuniorLP, isCustomer } = user.roles;
  const isProviderLp = isSeniorLP || isJuniorLP;

  const showBankRegisterFieldLp = isProviderLp && DIRECT_PAYMENT_CUSTOMER_LP;

  useEffect(() => () => clearInterval(timeLeftInterval), [timeLeftInterval]);

  useEffect(() => {
    if (network) {
      const fetchCurrencyRate = async () => {
        await getNativeCurrencyRate({ network })
          .then((feeCurRate) => {
            form.setFieldValue("feeCurRate", feeCurRate);
          })
          .catch((e) => {
            console.error(e);
          });
      };

      fetchCurrencyRate();
    }
  }, [form, network]);

  useEffect(() => {
    if (companyId && isImpContract) {
      bankAccountsStore.fetchBankAccounts(companyId).then(() => {
        if (bankAccountsStore.bankAccounts.length > 0) {
          const defaultAccount = bankAccountsStore.bankAccounts.find((item) => item.isDefault);

          const bankAccountByContractBik = bankAccountsStore.bankAccounts.find(
            (item) => item.bik === bidCard.bid.contract.bankBik
          );

          if (bankAccountByContractBik) {
            form.setFieldsValue({
              accountId: bankAccountByContractBik.id,
            });
            setSelectedAccountId(bankAccountByContractBik.id);
          } else {
            if (defaultAccount?.id) {
              form.setFieldsValue({
                accountId: defaultAccount.id,
              });
              setSelectedAccountId(defaultAccount.id);
            }
          }
        }
      });
    }
  }, [bankAccountsStore, bidCard.bid.contract.bankBik, companyId, form, isImpContract]);

  useEffect(() => {
    if (companyId && isExpContract) {
      companyCard
        .fetchCompanyCardById(companyId)
        .then((card) => {
          if (card) {
            const { wallets } = card;
            const currentWallet = wallets?.find((wallet) => wallet.network === network);
            if (currentWallet) {
              setDestinationWalletFromCard(currentWallet);
              form.setFieldValue("destinationWallet", currentWallet.address);
              setDestinationWalletValue(currentWallet.address ?? "");
            } else {
              setIsUseDefaultWallet(false);
            }
          }
        })
        .catch((e) => {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        });
    }
  }, [companyCard, companyId, form, isExpContract, network, openNotification]);

  useEffect(() => {
    if (offerStep === OFFER_STEP.OFFER) {
      setCanSendCode(canSendSms(OPERATION_TYPE.QUOTE_CREATE));
      if (!canSendCode) {
        const message = getLastSmsData(OPERATION_TYPE.QUOTE_CREATE);
        form.setFieldValue("rate", JSON.parse(message?.message ?? "").rate);
        setTimeLeft(SMS_CODE_RETRY_DELAY_IN_SECONDS);
        const interval = setInterval(() => setTimeLeft((value) => value - 1), 1000);
        setTimeLeftInterval(interval);
      }
    }
  }, [canSendCode, form, offerStep]);

  useEffect(() => {
    const fetchDefaultQuoteExpTime = async () => {
      setLoading(true);
      await getDefaultQuoteExpTime()
        .then((data) => {
          if (!!data) {
            const expDuration = convertFromISOHourMinuteDuration(data);
            if (expDuration[0] === 0 && expDuration[1] === 0) {
              setExpTimeFrame(true);
            } else {
              form.setFieldsValue({
                hours: expDuration[0].toString(),
                minutes: expDuration[1].toString(),
              });
              getExpTime();
            }
          }
        })
        .catch((e) => {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        })
        .finally(() => setLoading(false));
    };
    fetchDefaultQuoteExpTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleChangeFrame: CheckboxProps["onChange"] = (e) => {
    setExpTimeFrame(e.target.checked);
    if (e.target.checked) {
      form.setFieldValue("minutes", "");
      form.setFieldValue("hours", "");
    }
    getExpTime();
  };

  const { riskLevel, amlId, riskStatus } = useWalletRisk(
    {
      walletAddress: form.getFieldValue("destinationWallet"),
      requestId: bidCard.bid.id,
      userId: user.userInfo?.id,
    },
    canCheckWallet
  );

  useEffect(() => {
    if (amlId && !walletAmlIdList.some((item) => amlId === item)) {
      setWalletAmlIdList([...walletAmlIdList, amlId]);
    }
  }, [amlId, walletAmlIdList]);

  useEffect(() => {
    if (riskLevel && riskStatus) {
      setSavedRiskLevel(riskLevel);
      setIsWalletChecked(true);
      setCheckedWallet(form.getFieldValue("destinationWallet"));
      setCanCheckWallet(false);
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.SUCCESS) {
        offerStepHandler();
      }
      if (riskStatus === REQUEST_CHECK_STATUS_ENUM.APPROVE_NEEDED) {
        if ([RISK_LEVEL_ENUM.MEDIUM, RISK_LEVEL_ENUM.TIMEOUT].includes(riskLevel)) {
          setLoading(false);
          setButtonDisabled(false);
        } else {
          setLoading(false);
          setButtonDisabled(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskLevel]);

  const generateMessage = () => {
    const values = form.getFieldsValue();

    const message = defaultMessage({
      bid: bidCard.bid,
      user,
      rate: values.rate,
      destinationWallet: form.getFieldValue("destinationWallet"),
      quoteModal: true,
      showLpInfo: isProviderLp,
    });

    return message;
  };

  const generateSignature = async (currentMessage?: Record<string, any>) => {
    const message = currentMessage ?? generateMessage();

    const { signature: newSignature, jsonMessage } = await signatureMessage({
      userId: user.userInfo?.id,
      companyId: user.userInfo?.company_id,
      message,
      user,
      actionModals,
    });

    const messageHash = await hashMessage(jsonMessage);

    return {
      signature: newSignature,
      message: jsonMessage,
      hash: messageHash,
    };
  };

  const startOtpCountdown = () => {
    clearInterval(timeLeftInterval);
    setTimeLeft(SMS_CODE_RETRY_DELAY_IN_SECONDS);
    const interval = setInterval(() => setTimeLeft((value) => value - 1), 1000);
    setTimeLeftInterval(interval);
    setOfferStep(OFFER_STEP.SMS);

    form.setFieldsValue({
      otp: "",
    });
  };

  const getExpTime = () => {
    const values = form.getFieldsValue();

    if (!validateExpirationTime(values.hours, values.minutes)) {
      setExpTime("");
      return;
    }

    setExpTime(getExpirationTime(values.hours, values.minutes));
  };

  const sendSms = async () => {
    setLoading(true);
    let canSendSmsCode = canSendSms(OPERATION_TYPE.QUOTE_CREATE);

    if (canSendSmsCode) {
      const signatureData = await generateSignature();
      setCurrentMessageHash(signatureData.hash);
      const formData = JSON.stringify({ message: signatureData.message });

      await generateOperation(OPERATION_TYPE.QUOTE_CREATE, formData)
        .then(() => {
          if (signatureData) {
            markSmsSend(OPERATION_TYPE.QUOTE_CREATE, signatureData.message);
          }
          startOtpCountdown();
        })
        .catch((e) => {
          if (e.type === "/otp-error") {
            startOtpCountdown();
            openNotification({
              description: e.title || `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          } else {
            openNotification({
              description: parseErrorMessage(e),
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      const message = getLastSmsData(OPERATION_TYPE.QUOTE_CREATE);

      if (!message) {
        openNotification({
          description: "Не удалось найти предыдущий message, попробуйте позже",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      } else {
        openNotification({
          description: `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        startOtpCountdown();
      }
    }
  };

  let numberPost = useRef(0);

  const offerQuoteHandler = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const message = getLastSmsData(OPERATION_TYPE.QUOTE_CREATE);
    const signatureData = await generateSignature(JSON.parse(message?.message ?? ""));

    const formData = {
      accountId: !showBankRegisterFieldLp ? undefined : values.accountId,
      otp: values.otp,
      feeCurRate: values.feeCurRate,
      ...signatureData,
      destinationWallet: isUseDefaultWallet ? undefined : values.destinationWallet,
      quoteExpDuration: +values.hours || +values.minutes ? convertToISODuration(values.hours, values.minutes) : null,
      walletAmlIdList,
    };

    await createQuote(formData)
      .then(() => {
        openNotification({
          message: "Котировка создана успешно",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });

        handleCancelOffer();
        form.resetFields();
        clearSmsData(OPERATION_TYPE.QUOTE_CREATE);
        //убрать запрос quotes.fetch() после реализации вебсокетов
        setTimeout(() => {
          bidCard.fetchCardById(bidCard.bid.id);
          quotes.fetch();
        }, 1500);
      })
      .catch(async (e) => {
        if (e.title === "Недействительная подпись" || e.title === "Invalid signature") {
          if (numberPost.current === 0) {
            numberPost.current++;
            await clearCertDB(user.userInfo?.id, user.userInfo?.company_id);
            offerQuoteHandler();
          } else {
            actionModals.closeAll();
            user.error = "Сервис временно неисправен, попробуйте позже";
            user.logout();
          }
        } else {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getSubmitButtonText = (): string => {
    switch (offerStep) {
      case OFFER_STEP.OFFER:
        return "Предложить котировку";
      case OFFER_STEP.CHECKING_WALLET:
        return "Подтвердить";
      case OFFER_STEP.SMS:
        return "Подписать Электронной Подписью";
      default:
        return "";
    }
  };

  const getCancelButtonText = (): string => {
    switch (offerStep) {
      case OFFER_STEP.CHECKING_WALLET:
        return "Вернуться к форме";
      default:
        return "Отмена";
    }
  };

  const handleCancelOffer = () => {
    if (offerStep === OFFER_STEP.CHECKING_WALLET) {
      setOfferStep(OFFER_STEP.OFFER);
    } else {
      actionModals.closeLastModal();
    }
  };

  const offerStepHandler = () => {
    if (!isUseDefaultWallet) {
      setLoading(false);
      setOfferStep(OFFER_STEP.CHECKING_WALLET);
    } else {
      sendSms();
    }
  };

  const bankAlertMessage = getBankAlertMessage(
    bidCard.bid.contract.bankBik,
    bankAccountsStore.bankAccounts,
    selectedAccountId
  );

  const getSubmitButtonTooltip = () => {
    if (buttonDisabled) {
      return CHANGE_WALLET_ADDRESS_MESSAGE;
    }

    if (offerStep === OFFER_STEP.SMS) {
      return !DIRECT_PAYMENT_CUSTOMER_LP
        ? "Данное действие является Юридически значимым"
        : "Данное действие является предложением условий в рамках Договора купли-продажи ЦВ и является юридически значимым";
    }

    return undefined;
  };

  const modalTitle =
    bidCard.bid.contract.direction === CONTRACT_DIRECTION_ENUM.EXP ? "Котировка на покупку" : "Котировка на продажу";

  const whiteListWalletCheck = (walletAddress: string, whitelistEnabled: boolean) => {
    if (!isValidNetworkAddress(bidCard.bid.network as WALLET_NETWORK, walletAddress)) {
      setIsCheckWhiteListWallet(false);
      setWhitelistError(null);
      return;
    }

    checkWalletWhitelist({
      destinationWallet: walletAddress,
      network: bidCard.bid.network as "ETH" | "TRX",
    })
      .then((result) => {
        if (result.allowed) {
          setIsCheckWhiteListWallet(true);
        } else {
          setIsCheckWhiteListWallet(false);
          setWhitelistError("Указанный адрес отсутствует в белом списке");
        }
      })
      .catch((e: any) => {
        setIsCheckWhiteListWallet(false);
        setWhitelistError(null);
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      });
  };

  useDebounce(whiteListWalletCheck, [destinationWalletValue, isWhitelistEnabled], { wait: 2000 });

  return (
    <>
      <Flex vertical>
        <Form
          form={form}
          name="offerQuote"
          initialValues={{
            rate: "",
            feeCurRate: "",
            otp: "",
            hours: "",
            minutes: "",
          }}
          onValuesChange={(changedValues) => {
            if (changedValues.hours !== undefined || changedValues.minutes !== undefined) {
              getExpTime();
            }
            if (changedValues.destinationWallet !== undefined) {
              setDestinationWalletValue(changedValues.destinationWallet ?? "");
              setWhitelistError(null);
              setIsCheckWhiteListWallet(false);
            }
          }}
          onFinish={() => {
            if (offerStep === OFFER_STEP.OFFER) {
              // window.updateOnboardingElements?.();

              if (isExpContract) {
                const walletChecked = isWalletChecked && checkedWallet === form.getFieldValue("destinationWallet");

                if (walletChecked) {
                  offerStepHandler();
                } else {
                  setLoading(true);

                  setIsWalletChecked(false);
                  setCanCheckWallet(true);
                }
              } else {
                sendSms();
              }
            }
            if (offerStep === OFFER_STEP.CHECKING_WALLET) {
              sendSms();
            }
            if (offerStep === OFFER_STEP.SMS) {
              offerQuoteHandler();
            }
          }}
        >
          <Flex vertical gap="middle" hidden={isHiddenFormItems}>
            <Typography.Title level={1} className="margin-none" hidden={isHiddenFormItems}>
              {modalTitle}
            </Typography.Title>
            <Flex
              vertical
              hidden={isHiddenFormItems}
              id={contract.direction === "EXP" ? "create-exp-quote-amount" : "create-imp-quote-amount"}
            >
              <UsdtRateInput
                name="rate"
                placeholder={`Цена ${DIRECTION_TEXT[contract.direction]} рублей за 1 ${currency}`}
                disabled={loading}
                hidden={isHiddenFormItems}
                cryptoSymbol={currency}
              />

              <Form.Item
                name="feeCurRate"
                rules={[_isRequired("Цена обязательна для ввода"), _isNumber(), _greaterThan(0)]}
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
                hidden={isHiddenFormItems}
              >
                <NumberInput maxDecimals={2} placeholder={`Цена продажи в рублях за 1 ${network}`} disabled={loading} />
              </Form.Item>
            </Flex>

            <Flex vertical hidden={isHiddenFormItems}>
              <Typography.Text strong hidden={isHiddenFormItems} className="margin-bottom-xs">
                Срок действия котировки
              </Typography.Text>
              <ExpTimeInput disabled={loading || expTimeFrame} hidden={isHiddenFormItems} id="create-quote-duration" />
              <Typography.Text type="secondary" hidden={isHiddenFormItems} className="margin-bottom-xs">
                {expTimeFrame ? null : `Котировка будет валидна до ${dateFormat(expTime, true)}`}
              </Typography.Text>
              {!isHiddenFormItems && (
                <Flex id="create-quote-duration-checkbox">
                  <Tooltip title="Котировка не ограничена во времени">
                    <DefaultCheckbox
                      onChange={handleChangeFrame}
                      className="margin-bottom-md"
                      checked={expTimeFrame}
                      disabled={loading}
                    >
                      Бессрочная
                    </DefaultCheckbox>
                  </Tooltip>
                </Flex>
              )}
            </Flex>

            {isImpContract && bankAccountsStore.bankAccounts.length > 0 && showBankRegisterFieldLp && (
              <Flex vertical hidden={isHiddenFormItems} id="create-quote-bank-account-select">
                <Typography.Text strong className="margin-bottom-xs" hidden={isHiddenFormItems}>
                  Банковские реквизиты для получения рублевого платежа
                </Typography.Text>
                <Form.Item
                  name="accountId"
                  rules={[_isRequired("Банковские реквизиты обязательны для ввода")]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  className="full-width"
                  children={
                    <DefaultSelect
                      placeholder="Реквизиты"
                      disabled={loading}
                      size="large"
                      onChange={(value) => setSelectedAccountId(value)}
                    >
                      {renderBankOptions(bankAccountsStore.bankAccounts)}
                    </DefaultSelect>
                  }
                  hidden={isHiddenFormItems}
                />
                {bankAlertMessage && offerStep === OFFER_STEP.OFFER && (
                  <Alert message={bankAlertMessage} type="warning" showIcon />
                )}
              </Flex>
            )}

            {isExpContract && (
              <Flex vertical hidden={isHiddenFormItems} id="create-quote-bank-wallet-input">
                <Form.Item
                  name="destinationWallet"
                  rules={
                    !isUseDefaultWallet
                      ? [_isRequired("Поле обязательно для ввода"), _isValidNetworkAddress(bidCard.bid.network)]
                      : []
                  }
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  hidden={isHiddenFormItems}
                >
                  <DefaultInput
                    placeholder={`Кошелек получателя${
                      bidCard.bid.network ? ` в сети ${NETWORK_TEXT[bidCard.bid.network]}` : ""
                    }`}
                    disabled={loading || isUseDefaultWallet}
                    onChange={(e) => {
                      if (buttonDisabled) {
                        setButtonDisabled(false);
                      }
                      if (
                        e.target.value === checkedWallet &&
                        (savedRiskLevel === RISK_LEVEL_ENUM.HIGH || savedRiskLevel === RISK_LEVEL_ENUM.SEVERE)
                      ) {
                        setButtonDisabled(true);
                      }
                    }}
                    mask={trimMask}
                  />
                </Form.Item>
                {!!whitelistError && (
                  <Col>
                    {
                      <CustomAlert
                        showIcon
                        message={<Typography.Text strong>{whitelistError}</Typography.Text>}
                        type="error"
                      />
                    }
                  </Col>
                )}
                {/*
                 Удалить после тестирования
                <Form.Item hidden={isHiddenFormItems}>
                  <Tooltip trigger="hover" placement="top" title="Кошелек по умолчанию можно изменить в Профиле">
                    <DefaultCheckbox
                      onChange={(checkbox) => {
                        setIsUseDefaultWallet(checkbox.target.checked);
                        setIsWalletChecked(false);

                        if (checkbox.target.checked) {
                          form.setFieldValue("destinationWallet", destinationWalletFromCard?.address);
                        } else {
                          form.resetFields(["destinationWallet"]);
                        }

                        if (buttonDisabled) {
                          setButtonDisabled(false);
                        }
                      }}
                      checked={isUseDefaultWallet}
                      disabled={!destinationWalletFromCard || companyCard.isPending || loading}
                    >
                      Использовать кошелек по умолчанию
                    </DefaultCheckbox>
                  </Tooltip>
                </Form.Item> */}
                {isWalletChecked &&
                  !loading &&
                  savedRiskLevel !== RISK_LEVEL_ENUM.LOW &&
                  offerStep === OFFER_STEP.OFFER &&
                  savedRiskLevel &&
                  !(
                    (savedRiskLevel === RISK_LEVEL_ENUM.HIGH ||
                      savedRiskLevel === RISK_LEVEL_ENUM.SEVERE ||
                      savedRiskLevel === RISK_LEVEL_ENUM.UNDEFINED ||
                      savedRiskLevel === RISK_LEVEL_ENUM.TIMEOUT) &&
                    !buttonDisabled
                  ) && <AmlAlert amlMessage={getWalletRiskMessage(savedRiskLevel)} />}
              </Flex>
            )}
          </Flex>

          {offerStep === OFFER_STEP.CHECKING_WALLET && (
            <Flex vertical gap="middle">
              <Typography.Title level={1} className="margin-none">
                Внимательно проверьте введенный адрес криптокошелька
              </Typography.Title>
              <Typography.Text className="text-middle">{form.getFieldValue("destinationWallet")}</Typography.Text>
            </Flex>
          )}

          {offerStep === OFFER_STEP.SMS && (
            <Flex vertical>
              <Typography.Title level={5} className="margin-none">
                Предложение условий на {isExpContract ? "покупку " : "продажу "}
                цифровой валюты со следующими параметрами:
              </Typography.Title>
              <PageMessage
                bid={bidCard.bid}
                user={user}
                rate={form.getFieldsValue().rate}
                destinationWallet={form.getFieldValue("destinationWallet")}
                messageHash={currentMessageHash}
                showLpInfo={isProviderLp}
                showCustomerInfo={isCustomer}
                quoteModal={isProviderLp}
              />

              <Typography.Title level={5} className="text-center">
                Подтвердите действие кодом из смс:
              </Typography.Title>

              <Flex vertical>
                <OtpInput disabled={loading} className="margin-top-xs" onSendSms={sendSms} loading={loading} />
                <OtpTimeLeft onResend={sendSms} loading={loading} shouldStartCountdown={!!timeLeftInterval} />
              </Flex>
              <Divider />
            </Flex>
          )}

          <Form.Item className="margin-top-md">
            <Flex gap="large">
              <CustomButton
                id={offerStep === OFFER_STEP.OFFER ? "create-quote-sign-button" : undefined}
                htmlType="submit"
                disabled={loading || buttonDisabled || (isExpContract && !isCheckWhiteListWallet)}
                loading={loading}
                tooltip={getSubmitButtonTooltip()}
              >
                {getSubmitButtonText()}
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCancelOffer}
                className="full-width"
              >
                {getCancelButtonText()}
              </CustomButton>
            </Flex>
            {isExpContract && !isWalletChecked && loading && offerStep === OFFER_STEP.OFFER && (
              <Col className="margin-top-xs">
                <Typography.Text className="text-description">Идет проверка кошелька</Typography.Text>
              </Col>
            )}
            {isExpContract && savedRiskLevel === RISK_LEVEL_ENUM.LOW && offerStep === OFFER_STEP.OFFER && (
              <Col className="margin-top-xs">
                <Typography.Text className="text-description">Проверка кошелька пройдена</Typography.Text>
              </Col>
            )}
          </Form.Item>
        </Form>
      </Flex>
    </>
  );
});
